import React, { useState, useContext, useEffect, useRef } from "react";
import { Tooltip } from "../Tooltip";
import styles from "./federationTiles.module.css";
import OrgPlaceholderImage from "../../assets/org_placeholder.jpg";
import { ColorContext } from "../../contexts/color-context/ColorContext";
import { useAsyncFn } from "../../hooks/use-async-fn";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import SimpleBack from "../../assets/simpleBackIcon.svg";
import { useHistory } from "react-router-dom";
import { useSidebar } from "../NewSideBar/SidebarContext";
import { useFederation } from "../FederationSelection/FederationContext";
import InforIcon from "../../assets/InfoCircleIcon";
import InfoPopup from "../InfoPopup";
import LayoutContainer from "../LayoutContainer";
import { getFirstAccessibleRoute, formatDateOnly } from "../../utils";
import config from "../../config";
import { FEATURES } from "../../onam/features";
import { FED_PLACEHOLDER, ORG_PLACEHOLDER } from "../../onam/placeholders"; // brand glyph stand-ins (ONAM skin)
import { fedTiles, ofBrand } from "./grouping";

const PAGE_SIZE = 8;

const formatDate = (value: string | undefined): string => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  return formatDateOnly(d);
};

const FederationTiles = ({ authContext }) => {
  const history = useHistory();
  const { appMetadata, getLogoFederationID, getFednDetails, getFederations } =
    authContext;
  const colorContext = useContext(ColorContext);
  const { setFedPlatformName } = colorContext;
  const { setFedId } = useFederation();
  const { setBreadcrumbItems } = useBreadcrumb();
  const storedPermissions = JSON.parse(localStorage.getItem("userPermissions"));
  const [selectedTile, setSelectedTile] = useState(null);
  const { mainMenuItems, setActiveSubItem, setIsSideBarMenuDisabled } =
    useSidebar();
  const [fedOrgs, setFedOrgs] = useState([]);

  /* ── Search state ────────────────────────────────────────── */
  const [searchName, setSearchName] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstSearchRender = useRef(true);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBrand(null);
    setSearchName(value);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 400);
  };
  // a brand's group tile opens that brand: a search for it, showing only its own federations (ONAM skin)
  const [brand, setBrand] = useState<string | null>(null);
  const openBrand = (b: string) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setBrand(b); setSearchName(b); setDebouncedSearch(b);
  };
  const closeBrand = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setBrand(null); setSearchName(""); setDebouncedSearch("");
  };
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  let tenantObj = {};
  tenantObj = appMetadata ? Object.values(appMetadata) : [];
  const organizationId = tenantObj[0]?.organization;
  const userRole = localStorage.getItem("userRole");
  const metadataRoles: string[] = (tenantObj[0] as any)?.roles || [];
  const isPlatformOrAppAdmin =
    metadataRoles.includes(config.superUserRole) ||
    metadataRoles.includes(config.applicationAdminRole) ||
    userRole === config.superUserRole ||
    userRole === config.applicationAdminRole;
  /* ── Pagination state (admin only) ──────────────────────── */
  const [, setCurrentFetchArgs] = useState<{
    cursor?: string;
    prev_cursor?: string;
  }>({});
  const [prevCursor, setPrevCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const lastNextCursorRef = useRef<string | null>(null);
  const [isEndOfResults, setIsEndOfResults] = useState(false);

  const getLogoFederationIDFn = useAsyncFn({
    deferFn: !isPlatformOrAppAdmin ? getLogoFederationID : undefined,
  });
  const getFederationsFn = useAsyncFn({
    deferFn: isPlatformOrAppAdmin ? getFederations : undefined,
  });

  const [tileData, setTileData] = useState(
    getLogoFederationIDFn?.data?.items || [],
  );
  const getFednDetailsFn = useAsyncFn({
    deferFn: getFednDetails,
  });

  /* ── Fetch helper (admin only) ───────────────────────────── */
  const fetchPage = (
    cursorArgs: { cursor?: string; prev_cursor?: string } = {},
    name = "",
  ) => {
    setCurrentFetchArgs(cursorArgs);
    getFederationsFn.run({
      page_size: PAGE_SIZE,
      ...cursorArgs,
      ...(name ? { name } : {}),
    });
  };

  /* ── Fetch helper (non-admin) ────────────────────────────── */
  const fetchNonAdminPage = (
    cursorArgs: { cursor?: string; prev_cursor?: string } = {},
    name = "",
  ) => {
    getLogoFederationIDFn.run({
      organizationID: organizationId,
      page_size: PAGE_SIZE,
      ...(name ? { name } : {}),
      ...cursorArgs,
    });
  };

  const handleBack = () => {
    window?.history?.back();
  };
  useEffect(() => {
    if (selectedTile) {
      const selectedFed =
        tileData?.find((item) =>
          isPlatformOrAppAdmin
            ? item?.id === selectedTile.id
            : item?.federation_id === selectedTile.federation_id,
        ) ?? selectedTile;

      setFedOrgs(selectedFed?.organizations || []);
    } else {
      setFedOrgs([]);
    }
  }, [selectedTile, userRole, tileData, isPlatformOrAppAdmin]);

  const [logosFetched, setLogosFetched] = useState(false);

  /* ── Initial load ────────────────────────────────────────── */
  // For admin users: fetch immediately on mount (no org dependency).
  // For non-admin users: delay until organizationId is available, since
  // appMetadata (and therefore organizationId) is null during Auth0
  // re-authentication on browser refresh, causing the API call to fail
  // silently when organizationID is undefined.
  useEffect(() => {
    if (isPlatformOrAppAdmin) {
      fetchPage();
    } else if (organizationId) {
      fetchNonAdminPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  /* ── Search-triggered fetch ──────────────────────────────── */
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    setPrevCursor(null);
    setNextCursor(null);
    lastNextCursorRef.current = null;
    setIsEndOfResults(false);
    if (isPlatformOrAppAdmin) {
      fetchPage({}, debouncedSearch);
    } else {
      fetchNonAdminPage({}, debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    if (isPlatformOrAppAdmin) {
      if (getFederationsFn.data) {
        const items = getFederationsFn.data.items ?? [];
        setTileData(items);
        setLogosFetched(false);
        const pg = getFederationsFn.data.pagination ?? {};
        const newPrevCursor = pg.prev_cursor ?? null;
        setNextCursor(pg.next_cursor ?? null);
        if (items.length === 0 && !newPrevCursor && lastNextCursorRef.current) {
          setPrevCursor(lastNextCursorRef.current);
          setIsEndOfResults(true);
        } else {
          setPrevCursor(newPrevCursor);
          setIsEndOfResults(false);
        }
      }
    } else {
      if (getLogoFederationIDFn.data) {
        const items = getLogoFederationIDFn.data.items ?? [];
        setTileData(items);
        setLogosFetched(false);
        const pg = getLogoFederationIDFn.data.pagination ?? {};
        const newPrevCursor = pg.prev_cursor ?? null;
        setNextCursor(pg.next_cursor ?? null);
        if (items.length === 0 && !newPrevCursor && lastNextCursorRef.current) {
          setPrevCursor(lastNextCursorRef.current);
          setIsEndOfResults(true);
        } else {
          setPrevCursor(newPrevCursor);
          setIsEndOfResults(false);
        }
      }
    }
  }, [getLogoFederationIDFn.data, getFederationsFn.data, isPlatformOrAppAdmin]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchPlatformLogos = async () => {
      if (!tileData || tileData.length === 0 || logosFetched) return;

      console.log({ tileData });
      const updatedData = await Promise.all(
        tileData.map(async (tile) => {
          try {
            const response = await getFednDetailsFn.run({
              promiseFn: getFednDetails,
              federationID: isPlatformOrAppAdmin ? tile.id : tile.federation_id,
            });

            return {
              ...tile,
              platform_logo: response?.platform_logo || "",
              allowed_query_objectives:
                response?.allowed_query_objectives || [],
              // registry-service's EnableQueryAggregations is a *bool: null means
              // "predates this field" and is treated as enabled - only an explicit
              // false disables it. Keep the raw tri-state value here (don't
              // collapse undefined to false) so downstream consumers can apply
              // that same "not explicitly false" rule.
              enable_query_aggregations: response?.enable_query_aggregations,
              query_objective_parameters: response.query_objective_parameters,
              organizations:
                response?.organizations || tile.organizations || [],
              created_at: response?.created_at || tile.created_at,
            };
          } catch (error) {
            console.error("Error fetching federation details:", error);
            return tile;
          }
        }),
      );

      setTileData(updatedData);
      setLogosFetched(true);
    };

    fetchPlatformLogos();
  }, [tileData, logosFetched]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCreateAllowed = storedPermissions?.createfederation === "full";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: isCreateAllowed ? "/federation-selection" : "/choose-federation" },
      {
        label: "Federation Selection",
        path: isCreateAllowed ? "/federation-selection" : "/choose-federation",
      },
      { label: "Choose Existing Federation", path: "/choose-federation" },
    ]);
    const sideBarMenuToHighlight = mainMenuItems
      .flatMap((item) => item.subMenu)
      .find((subItem) =>
        subItem.urls.some((url) => url.includes("/choose-federation")),
      )?.label;
    setActiveSubItem(sideBarMenuToHighlight);
    setIsSideBarMenuDisabled({
      fed: false,
      ianda: true,
      cp: true,
      cr: true,
      pr: true,
      ca: false,
      ah: true,
      dm: true,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Pagination handlers ─────────────────────────────────── */
  const handleNextPage = () => {
    if (!nextCursor) return;
    lastNextCursorRef.current = nextCursor;
    if (isPlatformOrAppAdmin) {
      fetchPage({ cursor: nextCursor }, debouncedSearch);
    } else {
      fetchNonAdminPage({ cursor: nextCursor }, debouncedSearch);
    }
  };
  const handlePrevPage = () => {
    if (!prevCursor) return;
    lastNextCursorRef.current = null;
    if (isPlatformOrAppAdmin) {
      fetchPage({ prev_cursor: prevCursor }, debouncedSearch);
    } else {
      fetchNonAdminPage({ prev_cursor: prevCursor }, debouncedSearch);
    }
  };
  const isPending = isPlatformOrAppAdmin ? getFederationsFn.isPending : getLogoFederationIDFn.isPending;
  const isPrevDisabled = !prevCursor || isPending;
  const isNextDisabled = !nextCursor || isPending;

  const handleFedTileClick = (item) => {
    setFedPlatformName(item?.name);

    localStorage.setItem(
      "federationkey",
      isPlatformOrAppAdmin ? item?.id : item?.federation_id,
    );
    setFedId(isPlatformOrAppAdmin ? item?.id : item?.federation_id);

    localStorage.setItem("federationname", item?.name);

    localStorage.setItem(
      "allowedQueryObjectives",
      JSON.stringify(
        Array.from(
          new Set((item?.allowed_query_objectives ?? []).filter(Boolean)),
        ),
      ),
    );

    localStorage.setItem(
      "enableQueryAggregations",
      JSON.stringify(item?.enable_query_aggregations !== false),
    );

    const nextRoute = getFirstAccessibleRoute();

    history.push(nextRoute);
  };

  // a brand's federations as one tile, drawn as a small stack; opening it lists them all (ONAM skin)
  const renderGroupTile = (b: string, items: any[]) => (
    <div key={"group-" + b} className={`${styles.cardWrapper} fed-group`} role="button" tabIndex={0}
      aria-label={`${b}: ${items.length} federations. Open to see them all.`}
      onClick={() => openBrand(b)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openBrand(b); } }}>
      <div className={styles.card}>
        <div className={styles.tileContent}>
          <div className={`${styles.image} fed-stack`}>
            {items.slice(0, 4).map((it) => (
              <img key={isPlatformOrAppAdmin ? it?.id : it?.federation_id} src={it?.platform_logo || (FEATURES.curationSkin ? FED_PLACEHOLDER : OrgPlaceholderImage)} alt="" />
            ))}
          </div>
          <hr className={styles.hr} />
          <div className={styles.cardContent}>
            <h3 className={styles.title}>{b}</h3>
            <p className={styles.description}>{items.length} federations on this page</p>
          </div>
        </div>
      </div>
    </div>
  );

  // one federation tile
  const renderFedTile = (item, place?: React.CSSProperties) => (
                  <div
                    key={isPlatformOrAppAdmin ? item?.id : item?.federation_id}
                    className={`${styles.cardWrapper} ${
                      selectedTile === item ? styles.expanded : ""
                    }`}
                    // onMouseEnter, not onMouseOver: over fires again for every child the pointer crosses, and each one
                    // re-rendered the whole grid (ONAM optimisation pass, 17 Oct)
                    onMouseEnter={() => setSelectedTile(item)}
                    onMouseLeave={() => setSelectedTile(null)}
                    onClick={() => handleFedTileClick(item)}
                    data-tooltip={item?.name}
                    style={place}
                  >
                    <div className={styles.card}>
                      <div className={styles.tileContent}>
                        <img
                          src={item?.platform_logo || (FEATURES.curationSkin ? FED_PLACEHOLDER : OrgPlaceholderImage)}
                          alt={item?.name}
                          className={styles.image}
                        />

                        <hr className={styles.hr} />
                        <div className={styles.cardContent}>
                          <h3 className={styles.title}>{item?.name}</h3>
                          <p className={styles.description} title={formatDate(item?.created_at)}>
                            {formatDate(item?.created_at)}
                          </p>
                        </div>
                      </div>
                      {selectedTile === item && (
                        <div className={styles.expandedContent}>
                          {fedOrgs.map((org) => (
                            <div key={org?.id} className={styles.orgTile}>
                              <img
                                src={org?.logo_url || (FEATURES.curationSkin ? ORG_PLACEHOLDER : OrgPlaceholderImage)}
                                alt={org?.name}
                                className={styles.orgLogo}
                              />
                              <Tooltip text={org?.name} placement="top" style={{ display: "block", width: "100%" }}>
                              <p className={styles.orgName}>
                                {org?.name}
                              </p>
                              </Tooltip>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
  );

  return (
    <LayoutContainer>
      <div className={styles.container}>
        <div className={styles.actionsBar}>
          <button className={styles.backButton} onClick={handleBack}>
            <img src={SimpleBack} alt="back" />
            Back
          </button>

          <div className={styles.actionsRight}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search federations..."
              value={searchName}
              onChange={handleSearchChange}
            />
            <InforIcon
              className={styles.infoIcon}
              onClick={() => setShowInfoPopup(true)}
            />
          </div>
        </div>

        {showInfoPopup && (
          <InfoPopup
            displayText={
              "A federation is a logical silo where the brands and their data partners can be grouped together based on the business outcome. Brands will have access to run queries in federation, data partners give data sets per federation based on business usecase."
            }
            isOpen={showInfoPopup}
            onClose={() => setShowInfoPopup(false)}
            closeButtonLabel="Got it"
            headerText="Information"
            freeText
          />
        )}

        <div className={styles.content}>
          <h2
            style={{
              marginLeft: "30px",

              fontSize: "20px",
              fontWeight: 600,
            }}
            className={styles.header}
          >
            {FEATURES.curationSkin && brand ? (
              <span className="fed-brand-head">
                <button type="button" className="fed-brand-back" onClick={closeBrand}>All federations</button>
                <span aria-hidden="true">/</span>
                <span>{brand}</span>
              </span>
            ) : (
              "Choose an existing federation"
            )}
          </h2>
          <div className={styles.gridContainer}>
            {(isPlatformOrAppAdmin
              ? (getFederationsFn.isPending || getFederationsFn.isInitial)
              : (getLogoFederationIDFn.isPending || getLogoFederationIDFn.isInitial)) ? (
              <div className={styles.loaderContainer}>
                <div className={styles.spinner}></div>
              </div>
            ) : tileData.length === 0 ? (
              <p className={styles.emptyMessage}>
                {isEndOfResults
                  ? "No more federations found."
                  : "No federations found."}
              </p>
            ) : (
              FEATURES.curationSkin ? (
                <div className={`${styles.grid} fed-tiles`}>
                  {brand
                    ? (ofBrand(tileData, brand).length ? ofBrand(tileData, brand) : tileData).map((item) => renderFedTile(item))
                    : debouncedSearch
                      ? tileData.map((item) => renderFedTile(item))
                      : fedTiles(tileData).map((t) => (t.kind === "fed" ? renderFedTile(t.item) : renderGroupTile(t.brand, t.items)))}
                </div>
              ) : (
              <div className={styles.grid}>
                {tileData?.map((item) => renderFedTile(item))}
              </div>
              )
            )}
          </div>

          <div className={styles.paginationFooter}>
              <button
                className={
                  isPrevDisabled ? styles.navigationDisable : styles.prevPage
                }
                type="button"
                onClick={handlePrevPage}
              >
                Previous Page
              </button>
              <button
                className={
                  isNextDisabled ? styles.navigationDisable : styles.nextPage
                }
                type="button"
                onClick={handleNextPage}
              >
                Next Page
              </button>
            </div>
        </div>
      </div>
    </LayoutContainer>
  );
};

export default FederationTiles;
